import { fetchWithAuth, BASE_URL } from './client';

/**
 * Fetch leads sent to a partner (realtor or insurance agent)
 * Uses existing email history endpoints
 * @param {string} email - Partner's email address
 * @param {string} partnerType - 'realtor' or 'insurance'
 * @param {Object} options - Query options
 * @returns {Promise<{success: boolean, emails: Array, total: number}>}
 */
export async function fetchReferralLeads(email, partnerType, options = {}) {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit.toString());
    // Pass team_owner_id if provided - allows team owners to see all team leads
    if (options.team_owner_id) params.append('team_owner_id', options.team_owner_id);

    const endpoint = partnerType === 'insurance'
        ? 'insurance'
        : 'realtor';

    const queryString = params.toString();
    const url = `${BASE_URL}/api/leads/email-history/${endpoint}/${encodeURIComponent(email)}${queryString ? `?${queryString}` : ''}`;

    console.log(`[ReferralTracker] Calling API: ${url}`);

    const response = await fetchWithAuth(url, {
        cache: 'no-store'
    });

    console.log(`[ReferralTracker] Response status for ${email}: ${response.status}`);

    if (response.status === 404) {
        console.log(`[ReferralTracker] 404 - No data found for ${email}`);
        return { success: true, emails: [], total: 0 };
    }
    if (!response.ok) {
        console.error(`[ReferralTracker] Error response: ${response.status} ${response.statusText}`);
        throw new Error(`Failed to fetch partner leads: ${response.statusText}`);
    }
    try {
        const data = await response.json();
        // DEBUG: Log raw API response
        console.log(`[ReferralTracker] Raw API response for ${email}:`, JSON.stringify(data, null, 2));

        // Handle various response formats from API
        // API might return { emails: [...] }, { leads: [...] }, { data: [...] }, or just [...]
        let emails = [];
        if (Array.isArray(data)) {
            emails = data;
            console.log('[ReferralTracker] Response is array');
        } else if (Array.isArray(data.emails)) {
            emails = data.emails;
            console.log('[ReferralTracker] Found data.emails');
        } else if (Array.isArray(data.leads)) {
            emails = data.leads;
            console.log('[ReferralTracker] Found data.leads');
        } else if (Array.isArray(data.data)) {
            emails = data.data;
            console.log('[ReferralTracker] Found data.data');
        } else {
            console.log('[ReferralTracker] No recognized array format, data keys:', Object.keys(data || {}));
        }
        console.log(`[ReferralTracker] Fetched ${emails.length} leads for ${email}`);
        return { success: true, emails, total: data.total || emails.length };
    } catch (err) {
        console.error('[ReferralTracker] Error parsing response:', err);
        return { success: true, emails: [], total: 0 };
    }
}

/**
 * Update lead status by partner
 * PATCH /api/leads/{lead_id}/partner-update
 * @param {string} leadId - Lead ID to update
 * @param {string} status - New status (new, contacted, interested, converted, lost)
 * @param {string} partnerEmail - Partner's email for authorization
 * @returns {Promise<{success: boolean, lead: Object}>}
 */
export async function updateLeadPartnerStatus(leadId, status, partnerEmail) {
    const response = await fetchWithAuth(`${BASE_URL}/api/leads/${leadId}/partner-update`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            status,
            partner_email: partnerEmail
        })
    });

    if (response.status === 404) {
        throw new Error('Lead not found');
    }
    if (response.status === 403) {
        throw new Error('Not authorized to update this lead');
    }
    if (!response.ok) {
        throw new Error(`Failed to update lead status: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Add a note to a lead as a partner
 * POST /api/leads/{lead_id}/partner-notes
 * @param {string} leadId - Lead ID
 * @param {string} note - Note content
 * @param {string} partnerEmail - Partner's email for authorization
 * @returns {Promise<{success: boolean, note: Object}>}
 */
export async function addPartnerNote(leadId, note, partnerEmail) {
    // Validate note is not empty (prevents 422 validation errors from backend)
    const trimmedNote = note?.trim();
    if (!trimmedNote) {
        throw new Error('Note cannot be empty');
    }

    const response = await fetchWithAuth(`${BASE_URL}/api/leads/${leadId}/partner-notes`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            note: trimmedNote,
            partner_email: partnerEmail
        })
    });

    if (response.status === 404) {
        throw new Error('Lead not found');
    }
    if (response.status === 403) {
        throw new Error('Not authorized to add notes to this lead');
    }
    if (!response.ok) {
        throw new Error(`Failed to add note: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Get all partner notes for a lead
 * GET /api/leads/{lead_id}/partner-notes?partner_email={email}
 * @param {string} leadId - Lead ID
 * @param {string} partnerEmail - Partner's email for authorization
 * @returns {Promise<{notes: Array}>}
 */
export async function fetchPartnerNotes(leadId, partnerEmail) {
    const url = `${BASE_URL}/api/leads/${leadId}/partner-notes?partner_email=${encodeURIComponent(partnerEmail)}`;

    const response = await fetchWithAuth(url, {
        cache: 'no-store'
    });

    if (response.status === 404) {
        return { notes: [] };
    }
    if (!response.ok) {
        throw new Error(`Failed to fetch notes: ${response.statusText}`);
    }
    try {
        return await response.json();
    } catch {
        return { notes: [] };
    }
}

/**
 * Upload a document to a lead as a partner
 * POST /api/leads/{lead_id}/partner-documents
 * @param {string} leadId - Lead ID
 * @param {File} file - File to upload
 * @param {string} partnerEmail - Partner's email for authorization
 * @returns {Promise<{success: boolean, document: Object}>}
 */
export async function uploadPartnerDocument(leadId, file, partnerEmail) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('partner_email', partnerEmail);

    const response = await fetchWithAuth(`${BASE_URL}/api/leads/${leadId}/partner-documents`, {
        method: 'POST',
        body: formData
    });

    if (response.status === 404) {
        throw new Error('Lead not found');
    }
    if (response.status === 403) {
        throw new Error('Not authorized to upload documents to this lead');
    }
    if (!response.ok) {
        throw new Error(`Failed to upload document: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Get all partner documents for a lead
 * GET /api/leads/{lead_id}/partner-documents?partner_email={email}
 * @param {string} leadId - Lead ID
 * @param {string} partnerEmail - Partner's email for authorization
 * @returns {Promise<{documents: Array}>}
 */
export async function fetchPartnerDocuments(leadId, partnerEmail) {
    const url = `${BASE_URL}/api/leads/${leadId}/partner-documents?partner_email=${encodeURIComponent(partnerEmail)}`;

    const response = await fetchWithAuth(url, {
        cache: 'no-store'
    });

    if (response.status === 404) {
        return { documents: [] };
    }
    if (!response.ok) {
        throw new Error(`Failed to fetch documents: ${response.statusText}`);
    }
    try {
        return await response.json();
    } catch {
        return { documents: [] };
    }
}
